import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CustomMessageService } from '@admin/core/message.service';
import { ApplicationMessage } from '@admin/core/models/application-message';
import { ApplicationMessageSource } from '@admin/core/models/application-message-source';
import { MessageType } from '@admin/core/models/enum/message-type';
import { UrlConfigContainer } from '@admin/core/url-config';
import { FieldState } from '@admin/shared/models/field-state';
import { UrlConfigService } from '@hrra/core';
import { ConfigurationService } from '@admin/shared/services/configuration.service';
import { Utils } from '@admin/shared/utils';
import { ValidationUtils } from '@admin/shared/validation/validation-runner';
import { IValidator } from '@admin/shared/validation/validators';
import {
  UploaderOptions,
  UploadInput,
  UploadOutput,
} from '@angular-ex/uploader';
import { CustomerModel } from '../shared/models/customer-model';
import { CustomerService } from '../shared/services/customer.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-customer-edit-branding',
  templateUrl: './customer-edit-branding.component.html',
  styleUrls: ['./customer-edit-branding.component.less'],
  standalone: false,
})
export class CustomerEditBrandingComponent implements OnInit {
  @Output()
  public onUpdate: EventEmitter<CustomerModel> =
    new EventEmitter<CustomerModel>();

  @Input()
  public customer: CustomerModel;

  public editingCustomer: CustomerModel;

  private initialIdNumber: string;
  public attachmentMaxSize: number;

  public selectedDesktopImageFileName: string;
  public selectedMobileImageFileName: string;
  public selectedCoverImageFileName: string;

  uploadOptions: UploaderOptions;
  upload: EventEmitter<UploadInput>;
  public imageType: any;
  public colorType: any;

  public isColorPickerActive: boolean;
  public isAnnouncementListColorPickerActive: boolean;

  public fieldStates: {
    desktopImage: FieldState;
    mobileImage: FieldState;
    coverImage: FieldState;
    color: FieldState;
    listColor: FieldState;
    isActive: FieldState;
    isWithOwnAnnouncement: FieldState;
  };

  public get field() {
    return this.fieldStates;
  }
  constructor(
    private urlService: UrlConfigService<UrlConfigContainer>,
    private customerService: CustomerService,
    private messageService: CustomMessageService,
    private translationService: TranslateService,
    private config: ConfigurationService
  ) {
    this.upload = new EventEmitter<UploadInput>();
    this.editingCustomer = new CustomerModel();
    this.imageType = BrandingImageTypetEnum;
    this.colorType = BrandingColorTypeEnum;
    this.uploadOptions = { concurrency: 1, maxUploads: 1 };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customer'].currentValue != null) {
      this.editingCustomer = Utils.cloneObject(this.customer, CustomerModel);
      this.initialIdNumber = this.customer && this.customer.idNumber;
      this.initializeFieldsState();
    }
  }

  public initializeFieldsState() {
    this.fieldStates = {
      desktopImage: new FieldState(() => this.selectedDesktopImageFileName),
      mobileImage: new FieldState(() => this.selectedMobileImageFileName),
      coverImage: new FieldState(() => this.selectedCoverImageFileName),
      color: new FieldState(
        () =>
          this.editingCustomer.customerBranding &&
          this.editingCustomer.customerBranding.announcementDetailsBgColor
      ),
      listColor: new FieldState(
        () =>
          this.editingCustomer.customerBranding &&
          this.editingCustomer.customerBranding.announcementListColor
      ),
      isActive: new FieldState(
        () =>
          this.editingCustomer.customerBranding &&
          this.editingCustomer.customerBranding.isActive
      ),
      isWithOwnAnnouncement: new FieldState(
        () =>
          this.editingCustomer.customerBranding &&
          this.editingCustomer.customerBranding.isWithOwnAnnouncement
      ),
    };
  }

  public invokeValidation(validators: IValidator[], onValid?: () => void) {
    ValidationUtils.invokeValidation(
      validators,
      this.getFieldStatesObject(),
      onValid
    );
  }

  public onSaveClick() {
    let customer = this.editingCustomer;

    customer.customerBranding.announcementDesktopBgImage =
      this.selectedDesktopImageFileName != null
        ? this.selectedDesktopImageFileName
        : this.customer.customerBranding.announcementDesktopBgImage;
    customer.customerBranding.announcementMobileBgImage =
      this.selectedMobileImageFileName != null
        ? this.selectedMobileImageFileName
        : this.customer.customerBranding.announcementMobileBgImage;
    customer.customerBranding.customerCoverImage =
      this.selectedCoverImageFileName != null
        ? this.selectedCoverImageFileName
        : this.customer.customerBranding.customerCoverImage;

    this.editingCustomer.customerBranding.customerId = this.customer.customerId;
    this.editingCustomer.customerBranding.customerBrandingId =
      this.customer.customerBranding.customerBrandingId;
    let varlidators = this.getValidators();
    this.invokeValidation(varlidators, () => {
      if (this.customer.customerBranding.customerBrandingId == 0) {
        this.customerService
          .createCustomerBranding(customer.customerBranding)
          .subscribe(() => {
            this.messageService.pushMessage(<ApplicationMessage>{
              type: MessageType.Success,
              sourceId: ApplicationMessageSource.global,
              summary: '',
              detail: this.translationService.instant('message.SaveMessage'),
            });
            this.initializeFieldsState();
            this.onUpdate.emit(customer);
          });
      } else {
        this.customerService
          .updateCustomerBranding(customer.customerBranding)
          .subscribe(() => {
            this.messageService.pushMessage(<ApplicationMessage>{
              type: MessageType.Success,
              sourceId: ApplicationMessageSource.global,
              summary: '',
              detail: this.translationService.instant('message.SaveMessage'),
            });
            this.initializeFieldsState();
            this.onUpdate.emit(customer);
          });
      }
    });
  }

  public onCancelClick() {
    this.editingCustomer = Utils.cloneObject(this.customer, CustomerModel);
    this.initializeFieldsState();

    console.log(this.selectedDesktopImageFileName);
  }

  public onColorPicker(colorType: BrandingColorTypeEnum) {
    if (colorType == BrandingColorTypeEnum.AnnouncementList) {
      this.isAnnouncementListColorPickerActive =
        !this.isAnnouncementListColorPickerActive;
    }
    if (colorType == BrandingColorTypeEnum.CustomerBackground) {
      this.isColorPickerActive = !this.isColorPickerActive;
    }
  }

  private getFieldStatesObject(): { [index: string]: FieldState } {
    let result: { [index: string]: FieldState } = {};
    for (let p in this.fieldStates) {
      if (this.fieldStates.hasOwnProperty(p)) {
        result[this.fieldStates[p].fieldId] = this.fieldStates[p];
      }
    }

    return result;
  }

  ngOnInit() {
    const config = this.config.getCachedConfig();
    this.attachmentMaxSize = config.customerContractAttachmentMaxSize;
  }

  onUpload(output: UploadOutput, imageType: BrandingImageTypetEnum): void {
    if (output.type === 'allAddedToQueue') {
      const event: UploadInput = {
        type: 'uploadAll',
        url: this.urlService.urlConfig.api.data.customer.branding.uploadTempFile,
        method: 'POST',
      };
      this.upload.emit(event);
    } else if (output.type === 'done' && output.file.responseStatus == 200) {
      if (imageType === BrandingImageTypetEnum.desktop) {
        this.selectedDesktopImageFileName = output.file.response.fileName;
      } else if (imageType === BrandingImageTypetEnum.mobile) {
        this.selectedMobileImageFileName = output.file.response.fileName;
      } else if (imageType === BrandingImageTypetEnum.cover) {
        this.selectedCoverImageFileName = output.file.response.fileName;
      }
    }
  }

  private getValidators() {
    let fileMessage = 'file must be max ' + this.attachmentMaxSize + ' MB';

    return [];
  }
}

export enum BrandingImageTypetEnum {
  desktop = 1,
  mobile = 2,
  cover = 3,
}

export enum BrandingColorTypeEnum {
  CustomerBackground = 1,
  AnnouncementList = 2,
}
